import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  EProductSafetyGap,
  type IProductSafetyGapRow,
} from '@mahawi/eshop-common/src/types';
import { Select, Space, Tag, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { reportProductSafetyLoad } from '../reducers/product-safety/reducer';
import { SPACING } from '../theme/tokens';

interface IRow extends IProductSafetyGapRow {
  key: string;
}

const GAP_OPTIONS = Object.values(EProductSafetyGap).map(
  (gap: EProductSafetyGap) => ({ label: gap, value: gap }),
);

export const filterGapRows = (
  rows: IProductSafetyGapRow[],
  gaps: EProductSafetyGap[],
): IProductSafetyGapRow[] =>
  gaps.length === 0
    ? rows
    : rows.filter((row: IProductSafetyGapRow): boolean =>
        gaps.some((gap: EProductSafetyGap): boolean => row.gaps.includes(gap)),
      );

function ReportProductSafetyContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const gapRows = useSelector((s: RootState) => s.ProductSafety.gapRows);
  const inProcess = useSelector((s: RootState) => s.ProductSafety.inProcess);
  const [gapFilter, setGapFilter] = useState<EProductSafetyGap[]>([]);

  useEffect((): void => {
    dispatch(reportProductSafetyLoad());
  }, [dispatch]);

  const dataSource: IRow[] = useMemo(
    (): IRow[] =>
      filterGapRows(gapRows ?? [], gapFilter).map(
        (row: IProductSafetyGapRow): IRow => ({ ...row, key: row.productUuid }),
      ),
    [gapRows, gapFilter],
  );

  const columns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IRow, b: IRow): number => stringCompare(a.name, b.name),
      ...getColumnSearch<IRow>('name'),
      render: (_value: string, row: IRow): React.ReactElement => (
        <Link
          to={`/product/${row.productUuid}`}
          label={row.name}
          openInNewTab
        />
      ),
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      sorter: (a: IRow, b: IRow): number =>
        stringCompare(a.brand ?? '', b.brand ?? ''),
    },
    {
      title: 'Gaps',
      dataIndex: 'gaps',
      key: 'gaps',
      render: (gaps: EProductSafetyGap[]): React.ReactElement => (
        <Space size={SPACING.xs} wrap>
          {gaps.map((gap: EProductSafetyGap): React.ReactElement => (
            <Tag key={gap} color="orange">
              {gap}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Missing languages',
      dataIndex: 'missingLanguages',
      key: 'missingLanguages',
      render: (languages: string[]): string => languages.join(', '),
    },
  ];

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Product safety gaps"
        onReload={(): void => {
          dispatch(reportProductSafetyLoad());
        }}
        isReloadDisabled={inProcess}
      />

      <Space size={SPACING.lg} align="center">
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter by gap"
          style={{ minWidth: 320 }}
          options={GAP_OPTIONS}
          value={gapFilter}
          onChange={(value: EProductSafetyGap[]): void => setGapFilter(value)}
        />
        <Typography.Text>
          {dataSource.length} of {gapRows?.length ?? 0} products
        </Typography.Text>
      </Space>

      <Table
        columns={columns as Parameters<typeof Table>[0]['columns']}
        dataSource={dataSource}
        isLoading={inProcess && !gapRows}
      />
    </Space>
  );
}

export default ReportProductSafetyContainer;
